import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/layout';
import { useAuthStore, useAuthHeaders } from '@/lib/auth-store';
import { useGetMe } from '@workspace/api-client-react';
import { useToast } from '@/hooks/use-toast';
import { ShopifyTargetStoreSelector } from '@/components/shopify-target-store';
import {
  Bell, Users, MailCheck, UserX, Copy, Check, Trash2,
  Loader2, ToggleLeft, ToggleRight, ChevronDown,
  Code, Settings, RefreshCw, Send,
} from 'lucide-react';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

// ── Types ─────────────────────────────────────────────────────────────────────

interface Subscriber {
  id: string;
  storeId: string;
  email: string;
  productId: string;
  variantId: string;
  productTitle: string;
  variantTitle: string;
  productHandle: string;
  productImageUrl: string;
  subscribedAt: string;
  notifiedAt: string | null;
  unsubscribedAt: string | null;
}

interface Counts {
  total: number;
  pending: number;
  notified: number;
  unsubscribed: number;
}

interface NotifyMeSettings {
  enabled: boolean;
  buttonText: string;
  successMessage: string;
  emailSubject: string;
  fromName: string;
}

interface StoreOption {
  id: string;
  nickname: string;
  storeUrl: string;
}

const DEFAULT_SETTINGS: NotifyMeSettings = {
  enabled: true,
  buttonText: 'Notify Me When Available',
  successMessage: "We'll let you know when this is back in stock!",
  emailSubject: '{product} is back in stock!',
  fromName: '',
};

// ── Embed code generator ──────────────────────────────────────────────────────

function generateEmbedCode(storeUrl: string, settings: NotifyMeSettings): string {
  const apiUrl = `${window.location.origin}${BASE}/api/public/notify-me`;
  return `{% comment %} SoraFlo Back-in-Stock Widget — paste into your product form template {% endcomment %}
<div id="soraflo-notify-me"
     data-shop="{{ shop.permanent_domain }}"
     data-product-id="{{ product.id }}"
     data-product-title="{{ product.title | escape }}"
     data-product-handle="{{ product.handle }}"
     data-api="${apiUrl}"
     data-btn="${settings.buttonText.replace(/"/g, '&quot;')}"
     data-msg="${settings.successMessage.replace(/"/g, '&quot;')}">
  <div id="soraflo-wrap" style="display:none;margin-top:14px;">
    <p style="margin:0 0 10px;font-size:14px;color:#374151;font-family:inherit;">
      Get notified when this is back in stock
    </p>
    <form id="soraflo-form" style="display:flex;gap:8px;flex-wrap:wrap;align-items:center;">
      <input type="email" id="soraflo-email" placeholder="Your email address" required
             style="flex:1;min-width:200px;padding:10px 14px;border:1px solid #d1d5db;
                    border-radius:6px;font-size:14px;font-family:inherit;outline:none;" />
      <button type="submit" id="soraflo-btn"
              style="padding:10px 20px;background:#111827;color:#fff;border:none;
                     border-radius:6px;font-size:14px;font-weight:600;cursor:pointer;
                     white-space:nowrap;font-family:inherit;">
        ${settings.buttonText}
      </button>
    </form>
    <p id="soraflo-success" style="display:none;margin:8px 0 0;font-size:14px;color:#059669;font-family:inherit;">
      ${settings.successMessage}
    </p>
  </div>
</div>
<script>
(function(){
  var el=document.getElementById('soraflo-notify-me');
  var wrap=document.getElementById('soraflo-wrap');
  var form=document.getElementById('soraflo-form');
  var btn=document.getElementById('soraflo-btn');
  var success=document.getElementById('soraflo-success');
  if(!el||!wrap||!form)return;
  var api=el.dataset.api,shop=el.dataset.shop,pid=el.dataset.productId,
      handle=el.dataset.productHandle,title=el.dataset.productTitle,vid=null;

  function show(variantId,available){
    vid=String(variantId);
    wrap.style.display=available?'none':'block';
    success.style.display='none';
    form.style.display='flex';
    document.getElementById('soraflo-email').value='';
  }

  function checkByPage(){
    var vParam=new URLSearchParams(window.location.search).get('variant');
    fetch('/products/'+handle+'.js').then(function(r){return r.json();}).then(function(p){
      var v=vParam?p.variants.find(function(x){return String(x.id)===vParam;}):
        (p.variants.find(function(x){return!x.available;})||p.variants[0]);
      if(v)show(v.id,v.available);
    }).catch(function(){});
  }

  // Dawn/Shopify 2.0 variant change events
  ['variant:changed','variantChange'].forEach(function(ev){
    document.addEventListener(ev,function(e){
      var v=e.detail&&(e.detail.variant||e.detail);
      if(v&&v.id!=null)show(v.id,v.available);
    });
  });

  // URL change detection (Dawn pushState)
  var origPush=history.pushState;
  history.pushState=function(){origPush.apply(history,arguments);setTimeout(checkByPage,80);};
  window.addEventListener('popstate',checkByPage);

  checkByPage();

  form.addEventListener('submit',function(e){
    e.preventDefault();
    var email=document.getElementById('soraflo-email').value.trim();
    if(!email||!vid)return;
    btn.disabled=true;btn.textContent='Saving…';
    fetch(api+'/subscribe',{
      method:'POST',
      headers:{'Content-Type':'application/json'},
      body:JSON.stringify({shop:shop,email:email,productId:pid,variantId:vid,productTitle:title,productHandle:handle})
    }).then(function(r){
      if(r.ok){form.style.display='none';success.style.display='block';}
      else{btn.disabled=false;btn.textContent=el.dataset.btn||'Notify Me';}
    }).catch(function(){btn.disabled=false;btn.textContent=el.dataset.btn||'Notify Me';});
  });
})();
<\/script>`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function NotifyMe() {
  const authOpts = useAuthHeaders();
  const token = useAuthStore((s) => s.token);
  const { toast } = useToast();

  const [stores, setStores] = useState<StoreOption[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [counts, setCounts] = useState<Counts>({ total: 0, pending: 0, notified: 0, unsubscribed: 0 });
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'notified' | 'unsubscribed'>('pending');
  const [settings, setSettings] = useState<NotifyMeSettings>(DEFAULT_SETTINGS);
  const [settingsDraft, setSettingsDraft] = useState<NotifyMeSettings>(DEFAULT_SETTINGS);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [copiedEmbed, setCopiedEmbed] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [activeTab, setActiveTab] = useState<'subscribers' | 'settings' | 'embed'>('subscribers');

  // Load stores on mount
  useEffect(() => {
    if (!token) return;
    fetch(`${BASE}/api/stores`, { headers: authOpts.headers })
      .then((r) => r.json())
      .then((data: any[]) => {
        const opts = data.map((s) => ({ id: s.id, nickname: s.nickname || s.storeUrl, storeUrl: s.storeUrl }));
        setStores(opts);
        if (opts.length > 0) setSelectedStoreId(opts[0].id);
      })
      .catch(() => {});
  }, [token]);

  // Load settings when store changes
  useEffect(() => {
    if (!selectedStoreId || !token) return;
    fetch(`${BASE}/api/notify-me/settings?storeId=${selectedStoreId}`, { headers: authOpts.headers })
      .then((r) => r.json())
      .then((data: NotifyMeSettings) => {
        setSettings(data);
        setSettingsDraft(data);
      })
      .catch(() => {});
  }, [selectedStoreId, token]);

  // Load subscribers
  const loadSubscribers = useCallback(() => {
    if (!selectedStoreId || !token) return;
    setLoadingSubs(true);
    const params = new URLSearchParams({ storeId: selectedStoreId, limit: '100' });
    if (statusFilter !== 'all') params.set('status', statusFilter);
    fetch(`${BASE}/api/notify-me/subscribers?${params}`, { headers: authOpts.headers })
      .then((r) => r.json())
      .then((data) => {
        setSubscribers(data.subscribers || []);
        setCounts(data.counts || { total: 0, pending: 0, notified: 0, unsubscribed: 0 });
      })
      .catch(() => {})
      .finally(() => setLoadingSubs(false));
  }, [selectedStoreId, statusFilter, token]);

  useEffect(() => { loadSubscribers(); }, [loadSubscribers]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const r = await fetch(`${BASE}/api/notify-me/settings`, {
        method: 'PUT',
        headers: { ...authOpts.headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...settingsDraft, storeId: selectedStoreId }),
      });
      if (!r.ok) throw new Error('Save failed');
      const saved = await r.json();
      setSettings(saved);
      setSettingsDraft(saved);
      toast({ title: 'Settings saved' });
    } catch {
      toast({ title: 'Failed to save settings', variant: 'destructive' });
    } finally {
      setSavingSettings(false);
    }
  };

  const deleteSubscriber = async (id: string) => {
    setDeletingId(id);
    try {
      const r = await fetch(`${BASE}/api/notify-me/subscribers/${id}`, {
        method: 'DELETE',
        headers: authOpts.headers,
      });
      if (!r.ok) throw new Error();
      setSubscribers((prev) => prev.filter((s) => s.id !== id));
      setCounts((prev) => ({ ...prev, total: Math.max(0, prev.total - 1) }));
    } catch {
      toast({ title: 'Failed to remove subscriber', variant: 'destructive' });
    } finally {
      setDeletingId(null);
    }
  };

  const sendTestEmail = async () => {
    if (!selectedStoreId) return;
    setSendingTestEmail(true);
    try {
      const r = await fetch(`${BASE}/api/notify-me/test-email`, {
        method: 'POST',
        headers: { ...authOpts.headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storeId: selectedStoreId,
          fromName: settingsDraft.fromName,
          emailSubject: settingsDraft.emailSubject,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message ?? 'Send failed');
      toast({ title: 'Test email sent', description: `Delivered to ${data.sentTo}` });
    } catch (err: any) {
      toast({ title: 'Failed to send test email', description: err.message, variant: 'destructive' });
    } finally {
      setSendingTestEmail(false);
    }
  };

  const copyEmbed = () => {
    const selectedStore = stores.find((s) => s.id === selectedStoreId);
    const code = generateEmbedCode(selectedStore?.storeUrl ?? '', settingsDraft);
    navigator.clipboard.writeText(code).then(() => {
      setCopiedEmbed(true);
      setTimeout(() => setCopiedEmbed(false), 2000);
    });
  };

  const selectedStore = stores.find((s) => s.id === selectedStoreId);
  const embedCode = selectedStore ? generateEmbedCode(selectedStore.storeUrl, settingsDraft) : '';

  const statusTabs = [
    { key: 'pending',      label: 'Pending',      count: counts.pending },
    { key: 'notified',     label: 'Notified',      count: counts.notified },
    { key: 'unsubscribed', label: 'Unsubscribed',  count: counts.unsubscribed },
    { key: 'all',          label: 'All',            count: counts.total },
  ] as const;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-8">

        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center">
              <Bell className="w-5 h-5 text-violet-500" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Back-in-Stock Alerts</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Email customers automatically when out-of-stock items return
              </p>
            </div>
          </div>

          <ShopifyTargetStoreSelector
            stores={stores}
            value={selectedStoreId}
            onChange={setSelectedStoreId}
            label="Target Shopify store"
            className="w-full sm:w-72"
          />
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Subscribers', value: counts.total,       icon: Users,     color: 'text-blue-500',   bg: 'bg-blue-500/10' },
            { label: 'Awaiting Stock',    value: counts.pending,     icon: Bell,      color: 'text-amber-500',  bg: 'bg-amber-500/10' },
            { label: 'Notified',          value: counts.notified,    icon: MailCheck, color: 'text-green-500',  bg: 'bg-green-500/10' },
            { label: 'Unsubscribed',      value: counts.unsubscribed, icon: UserX,    color: 'text-rose-500',   bg: 'bg-rose-500/10' },
          ].map(({ label, value, icon: Icon, color, bg }) => (
            <div key={label} className="bg-card border border-border rounded-xl p-4">
              <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center mb-3`}>
                <Icon className={`w-4 h-4 ${color}`} />
              </div>
              <p className="text-2xl font-bold text-foreground">{value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
            </div>
          ))}
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 mb-6 border-b border-border">
          {[
            { key: 'subscribers', label: 'Subscribers', icon: Users },
            { key: 'settings',    label: 'Widget Settings', icon: Settings },
            { key: 'embed',       label: 'Embed Code', icon: Code },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key as any)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeTab === key
                  ? 'border-violet-500 text-violet-500'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* ── SUBSCRIBERS TAB ─────────────────────────────── */}
        {activeTab === 'subscribers' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              {/* Status filter pills */}
              <div className="flex gap-2">
                {statusTabs.map(({ key, label, count }) => (
                  <button
                    key={key}
                    onClick={() => setStatusFilter(key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      statusFilter === key
                        ? 'bg-violet-500 text-white'
                        : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {label} {count > 0 && <span className="ml-1 opacity-70">{count}</span>}
                  </button>
                ))}
              </div>

              <button
                onClick={loadSubscribers}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground px-2 py-1.5 rounded-lg hover:bg-muted transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Refresh
              </button>
            </div>

            {loadingSubs ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
              </div>
            ) : subscribers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center bg-card border border-border rounded-xl">
                <Bell className="w-10 h-10 text-muted-foreground mb-3 opacity-40" />
                <p className="text-sm font-medium text-foreground">
                  {statusFilter === 'pending'
                    ? 'No pending subscribers'
                    : statusFilter === 'notified'
                    ? 'No customers have been notified yet'
                    : 'No subscribers yet'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {statusFilter === 'pending'
                    ? 'All caught up — everyone has been notified, or no one has subscribed yet.'
                    : 'Once customers subscribe via your store, they will appear here.'}
                </p>
              </div>
            ) : (
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Product</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Customer</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Subscribed</th>
                      <th className="px-4 py-3 w-10" />
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.map((sub, i) => (
                      <tr key={sub.id} className={`border-b border-border last:border-0 hover:bg-muted/20 transition-colors ${i % 2 === 0 ? '' : 'bg-muted/10'}`}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-foreground truncate max-w-[220px]">{sub.productTitle}</p>
                          {sub.variantTitle && (
                            <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[220px]">{sub.variantTitle}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{sub.email}</td>
                        <td className="px-4 py-3">
                          {sub.unsubscribedAt ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-rose-500/10 text-rose-500 font-medium">
                              <UserX className="w-3 h-3" /> Unsubscribed
                            </span>
                          ) : sub.notifiedAt ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-500/10 text-green-500 font-medium">
                              <MailCheck className="w-3 h-3" /> Notified
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-500/10 text-amber-500 font-medium">
                              <Bell className="w-3 h-3" /> Pending
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">
                          {new Date(sub.subscribedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => deleteSubscriber(sub.id)}
                            disabled={deletingId === sub.id}
                            className="text-muted-foreground hover:text-rose-500 transition-colors disabled:opacity-40"
                            title="Remove subscriber"
                          >
                            {deletingId === sub.id
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <Trash2 className="w-4 h-4" />}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── SETTINGS TAB ────────────────────────────────── */}
        {activeTab === 'settings' && (
          <div className="max-w-xl">
            <div className="bg-card border border-border rounded-xl p-6 space-y-6">

              {/* Enable toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-foreground">Enable Back-in-Stock Alerts</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    When disabled, new subscriptions will be rejected
                  </p>
                </div>
                <button
                  onClick={() => setSettingsDraft((d) => ({ ...d, enabled: !d.enabled }))}
                  className={`w-12 h-6 rounded-full transition-colors relative ${
                    settingsDraft.enabled ? 'bg-violet-500' : 'bg-muted-foreground/30'
                  }`}
                >
                  <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${
                    settingsDraft.enabled ? 'left-7' : 'left-1'
                  }`} />
                </button>
              </div>

              <div className="border-t border-border pt-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Button Text</label>
                  <input
                    type="text"
                    value={settingsDraft.buttonText}
                    onChange={(e) => setSettingsDraft((d) => ({ ...d, buttonText: e.target.value }))}
                    placeholder="Notify Me When Available"
                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Success Message</label>
                  <input
                    type="text"
                    value={settingsDraft.successMessage}
                    onChange={(e) => setSettingsDraft((d) => ({ ...d, successMessage: e.target.value }))}
                    placeholder="We'll let you know when this is back in stock!"
                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Email Subject
                    <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                      Use <code className="bg-muted px-1 py-0.5 rounded text-[11px]">{'{product}'}</code> for product name
                    </span>
                  </label>
                  <input
                    type="text"
                    value={settingsDraft.emailSubject}
                    onChange={(e) => setSettingsDraft((d) => ({ ...d, emailSubject: e.target.value }))}
                    placeholder="{product} is back in stock!"
                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Email From Name
                    <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                      Defaults to your store name
                    </span>
                  </label>
                  <input
                    type="text"
                    value={settingsDraft.fromName}
                    onChange={(e) => setSettingsDraft((d) => ({ ...d, fromName: e.target.value }))}
                    placeholder={selectedStore?.nickname ?? 'Your store name'}
                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                  />
                </div>
              </div>

              <button
                onClick={saveSettings}
                disabled={savingSettings}
                className="w-full flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-500 text-white rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60"
              >
                {savingSettings && <Loader2 className="w-4 h-4 animate-spin" />}
                {savingSettings ? 'Saving…' : 'Save Settings'}
              </button>

              <div className="border-t border-border pt-5">
                <p className="text-xs text-muted-foreground mb-3">
                  Send a sample email to your account address to preview how the notification looks before going live.
                </p>
                <button
                  onClick={sendTestEmail}
                  disabled={sendingTestEmail || !selectedStoreId}
                  className="w-full flex items-center justify-center gap-2 bg-background border border-border hover:border-violet-500/50 hover:text-violet-500 text-foreground rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60"
                >
                  {sendingTestEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {sendingTestEmail ? 'Sending…' : 'Send Test Email'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── EMBED CODE TAB ──────────────────────────────── */}
        {activeTab === 'embed' && (
          <div className="max-w-3xl">
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
                <div>
                  <p className="text-sm font-semibold text-foreground">Shopify Theme Snippet</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Paste this into your <code className="bg-muted px-1 py-0.5 rounded text-[11px]">product-form.liquid</code> or <code className="bg-muted px-1 py-0.5 rounded text-[11px]">product.liquid</code> file,
                    inside your product form, right after the Add to Cart button.
                  </p>
                </div>
                <button
                  onClick={copyEmbed}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    copiedEmbed
                      ? 'bg-green-500/10 text-green-500'
                      : 'bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {copiedEmbed ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copiedEmbed ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <pre className="p-4 text-xs text-muted-foreground overflow-x-auto whitespace-pre-wrap leading-relaxed max-h-[440px] overflow-y-auto">
                {embedCode}
              </pre>
            </div>

            <div className="mt-4 bg-amber-500/5 border border-amber-500/20 rounded-xl px-4 py-3">
              <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">Installation tips</p>
              <ul className="mt-1.5 text-xs text-muted-foreground space-y-1 list-disc list-inside">
                <li>In Shopify Admin → Online Store → Themes → Edit code, find <code className="bg-muted px-1 py-0.5 rounded">product-form.liquid</code> (or <code className="bg-muted px-1 py-0.5 rounded">product.liquid</code> for older themes)</li>
                <li>Place the snippet after your Add to Cart or Buy Now button</li>
                <li>The widget automatically shows only when a variant is out of stock</li>
                <li>Re-copy this snippet after changing settings above to keep button text in sync</li>
              </ul>
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
}
